import axios from 'axios'
import qs from 'qs'
import jwtDecode from 'jwt-decode'
import moment from 'moment'

import router from '@/router'
import store from '@/store'
import { isString } from 'lodash'
import path from 'path'

const axiosInstance = axios.create({
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
})

axiosInstance.interceptors.request.use(async config => {
    config.baseURL = store.state.auth.backend?.api_url

    if (store.state.auth.jwt) {
        const jwt = await store.dispatch('auth/refreshTokenIfNecessary')
        if (jwt?.access && !config.headers.Authorization) {
            config.headers.Authorization = `JWT ${jwt.access}`
        }
    }
    return config
})

axiosInstance.interceptors.response.use(
    response => response,
    error => {
        if (
            error.response?.status === 403 &&
            !router.app.$route.matched[0].meta.isSharing
        ) {
            store.dispatch('auth/logout')
            router.push({ name: 'login' })
            window.location.reload()
        }
        return Promise.reject(error)
    }
)

const buildUrl = (url, opts) => {
    // Not sure if this works for all cases, but it does for now
    return url.startsWith('http')
        ? url
        : path.join(opts.apiVersion || 'v1', url)
}

const get = (url, config = {}, opts = {}) => {
    return axiosInstance.get(buildUrl(url, opts), config)
}

const getRecursively = async (url, config = {}, opts = {}) => {
    let response = await get(url, config, opts)
    while (response.data.next) {
        const prevResults = response.data.results
        const nextConfig = { ...config }
        // `next` already contains pagination query params (page/limit/etc).
        // Reusing `config.params` would append duplicates like repeated `limit`.
        delete nextConfig.params
        response = await get(response.data.next, nextConfig, opts)
        response.data.results.unshift(...prevResults)
    }
    return response
}

const post = (url, body, config = {}, opts = {}) => {
    return axiosInstance.post(buildUrl(url, opts), body, config)
}

const put = (url, body, config = {}, opts = {}) => {
    return axiosInstance.put(buildUrl(url, opts), body, config)
}

const patch = (url, body, config = {}, opts = {}) => {
    return axiosInstance.patch(buildUrl(url, opts), body, config)
}

const drop = (url, config = {}, opts = {}) => {
    return axiosInstance.delete(buildUrl(url, opts), config)
}

const getOverwrittenConfig = async () => {
    const tokenOverwritten = JSON.parse(localStorage.getItem('JWT-overwritten'))

    if (!tokenOverwritten) {
        return undefined
    }

    let accessToken

    if (moment.unix(jwtDecode(tokenOverwritten.access).exp).isAfter()) {
        accessToken = tokenOverwritten.access
    } else if (moment.unix(jwtDecode(tokenOverwritten.refresh).exp).isAfter()) {
        try {
            const { data } = await axios.post(
                store.state.auth.backend.jwt_url + 'refresh/',
                JSON.stringify({ refresh: tokenOverwritten.refresh }),
                { headers: { 'Content-Type': 'application/json' } }
            )

            if (data.access) {
                localStorage.setItem('JWT-overwritten', JSON.stringify(data))
                accessToken = data.access
            }
        } catch (error) {
            return undefined
        }
    }

    return accessToken
        ? { headers: { Authorization: `JWT ${accessToken}` } }
        : undefined
}

const parseError = error => {
    if (error.response?.data) {
        if (isString(error.response.data)) {
            return error.response.data
        }
        if (error.response.data.detail) {
            return error.response.data.detail
        }
        return Object.values(error.response.data)
            .reduce((acc, val) => {
                return [...acc, ...val]
            }, [])
            .join(', ')
    }
}

export default {
    get,
    getRecursively,
    post,
    put,
    drop,
    patch,
    getOverwrittenConfig,
    parseError,
}
